import React, { useState, useEffect, useRef, memo } from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonIcon,
  IonRow,
  IonCol,
  IonGrid,
  IonFooter,
  IonText,
  IonAlert,
} from "@ionic/react";
import { closeOutline, close } from "ionicons/icons";
import { v4 as uuidv4 } from "uuid";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings,
} from "capacitor-native-settings";
import PubSub from "pubsub-js";
import { send } from "../../../utils/sockets/asyncSocket";
import config from "../../../components/helpers/socket/config";
import { hapticsImpactMedium } from "../../../utils/core-utils/hapticUtils";
import { Alert } from "../../../pages/notifications";
import { pubsubEvents } from "../../../components/helpers/socket/pubsubEvent";
import expandIcon from "../../../theme/custom-icons/expand_more.svg";
import {
  FractionalToDecimalConverter,
  validatePrice,
} from "../common/utils/FractionalToDecimalConverterAndValidator";
import { DecimalToFractionalConverter } from "../common/utils/DecimalToFractionalConverterAndValidator";
import { getCapacitorPreference } from "../../../utils/core-utils/capPreferencesUtils";
import TickComponent from "./TickComponent";
import {
  formatPriceForScreen,
  removeCommasOnFocus,
} from "../common/utils/priceInputUtils";
import { parseTickGroupString } from "../common/utils/parseTickGroupString";
import { PriceErrorConstants } from "../common/utils/PriceErrorConstants";
import { PriceRegexValidator } from "../common/utils/PriceRegexValidator";
import {
  startMetric,
  endMetric,
} from "../../../utils/metric-utils/metricUtils";
import {
  MetricConstants,
  MetricTypeConstants,
} from "../../../components/Constants";
import {
  getFieldLabel,
  isPriceField,
  extractValueFromCondition,
} from "../../../utils/alertUtils";
import { CMEDfirebaseLogEvent } from "../../../utils/firebaseAnalytics/firebaseUtils";
import firebaseEventConfig from "../../../utils/firebaseAnalytics/firebaseEventConfig";

type Data = {
  b: { r: number; f: string };
  bb: { r: number; f: string };
  bo: { r: number; f: string };
  boq: { r: number; f: string };
  c: string;
  i: boolean;
  id: string;
  ii: number;
  l: any;
  lt: number;
  n: any;
  s: number;
  t: any;
  ts: string;
  wo: number;
  g: string;
  gi: string;
};

interface PriceAlertModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  tradingGridGroupData: Data | null;
  isEditMode?: boolean;
  alertData?: Alert;
}

const ticksIncrementorDecrement = (arr: any) => {
  const checkingKeysInObject = new Set();
  return arr?.filter((item: any) => {
    const key = item?.r;
    if (!checkingKeysInObject.has(key)) {
      checkingKeysInObject.add(key);
      return true;
    }
    return false;
  });
};

const removeDuplicatesFromTheFetchingTicks = (
  arr: any,
  newValues: any,
  arrayType: any,
) => {
  return {
    ...arr,
    [arrayType]: ticksIncrementorDecrement([...arr[arrayType], ...newValues]),
  };
};

const PriceAlertModal: React.FC<PriceAlertModalProps> = ({
  isOpen,
  onDidDismiss,
  tradingGridGroupData,
  isEditMode = false,
  alertData = null,
}) => {
  const [alertName, setAlertName] = useState("");
  const [value, setValue] = useState<string | number | undefined>(0);
  const [valueRaw, setValueRaw] = useState<number | undefined>(0);
  const [operator, setOperator] = useState("GT");
  const [alertType, setAlertType] = useState<string>("tradePrice");
  const [productType, setProductType] = useState<"FNO" | "BLOCK">("FNO");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [alertNameRequiredErrorMessage, setAlertNameRequiredErrorMessage] =
    useState("");
  const [valueRequiredErrorMessage, setValueRequiredErrorMessage] =
    useState("");
  const [priceType, setPriceType] = useState<string | undefined>(undefined);
  const [ticks, setTicks] = useState<{ up: any[]; down: any[] }>({
    up: [],
    down: [],
  });
  const priceRequestIds = useRef<string[]>([]);
  const [tickGroup, setTickGroup] = useState<string | undefined>(undefined);
  const [tickStrategy, setTickStrategy] = useState<string | undefined>(
    undefined,
  );
  const initialValueSetRef = useRef(false);
  const [showPushWarning, setShowPushWarning] = useState(false);

  const alertTypeRef = useRef<HTMLIonSelectElement>(null);
  const operatorRef = useRef<HTMLIonSelectElement>(null);

  const [initialFormState, setInitialFormState] = useState<{
    alertName: string;
    value: string | number | undefined;
    operator: string;
    alertType: string;
  } | null>(null);

  const _handleKeyDownForQty = (
    event: React.KeyboardEvent<HTMLIonInputElement>,
  ) => {
    if (event.key === "." || event.key === "," || event.key === "-") {
      event.preventDefault();
    }
  };

  const getPaddedFractionalValue = (val: number, pType: string) => {
    let converted = DecimalToFractionalConverter(val, pType);

    if (pType !== "NotFractional" && Number.isInteger(val)) {
      if (!converted.includes("'")) {
        switch (pType) {
          case "Halves":
          case "Eighths":
            return `${converted}'0`;
          case "ThirtySeconds":
          case "SixtyFourths":
            return `${converted}'00`;
          default:
            return `${converted}'000`;
        }
      }
    }

    if (pType === "EighthThirtySeconds") {
      const { valid, decimal_number } = FractionalToDecimalConverter(
        converted,
        pType,
      );
      if (!valid || Math.abs(decimal_number - val) > 0.00001) {
        const integer = Math.trunc(val);
        const remainder = Math.abs(val - integer);
        const thirtySeconds = Math.floor(remainder * 32 + 0.00001);
        const subRemainder = remainder * 32 - thirtySeconds;
        let eighths = Math.round(subRemainder * 8);

        if (eighths >= 4 && eighths <= 7) {
          eighths += 1;
        }

        const sign = val < 0 ? "-" : "";
        const absInt = Math.abs(integer);
        const nn = thirtySeconds.toString().padStart(2, "0");
        const s = eighths.toString();
        const candidate = `${sign}${absInt}'${nn}${s}`;

        const { valid: candidateValid, decimal_number: candidateDecimal } =
          FractionalToDecimalConverter(candidate, pType);
        if (candidateValid && Math.abs(candidateDecimal - val) < 0.00001) {
          converted = candidate;
        }
      }
    }

    if (pType !== "NotFractional") {
      const parts = converted.split("'");
      if (parts.length === 2) {
        if (
          [
            "HalfThirtySeconds",
            "QuarterThirtySeconds",
            "EighthThirtySeconds",
          ].includes(pType)
        ) {
          // Try padding to 3 digits
          const padded3 = parts[0] + "'" + parts[1].padStart(3, "0");
          let { decimal_number: dec3, valid: valid3 } =
            FractionalToDecimalConverter(padded3, pType);

          if (pType === "EighthThirtySeconds") {
            const p = padded3.split("'");
            if (p.length === 2 && p[1].length === 3) {
              const integer = parseInt(p[0], 10);
              const num = parseInt(p[1].substring(0, 2), 10);
              const sub = parseInt(p[1].substring(2), 10);
              if (!isNaN(integer) && !isNaN(num) && !isNaN(sub)) {
                const sign = padded3.startsWith("-") ? -1 : 1;
                let subVal = sub;
                if (sub === 5) subVal = 4;
                else if (sub === 7) subVal = 6;
                dec3 = sign * (Math.abs(integer) + num / 32 + subVal / 256);
                valid3 = true;
              }
            }
          }
          if (
            valid3 &&
            (Math.abs(dec3 - val) <= 0.0001 ||
              (Math.abs(val) < 0.03 &&
                parts[1].length === 2 &&
                parts[1].startsWith("0")))
          ) {
            return padded3;
          }
        }

        // Try padding to 2 digits
        const padded2 = parts[0] + "'" + parts[1].padStart(2, "0");
        const { decimal_number: dec2, valid: valid2 } =
          FractionalToDecimalConverter(padded2, pType);
        if (valid2 && Math.abs(dec2 - val) <= 0.0001) {
          return padded2;
        }
      }
    }
    const { valid } = FractionalToDecimalConverter(converted, pType);
    if (!valid && pType !== "NotFractional") {
      let tickSize = 0;
      switch (pType) {
        case "Halves":
          tickSize = 1 / 2;
          break;
        case "Eighths":
          tickSize = 1 / 8;
          break;
        case "ThirtySeconds":
          tickSize = 1 / 32;
          break;
        case "SixtyFourths":
          tickSize = 1 / 64;
          break;
        case "HalfThirtySeconds":
          tickSize = 1 / 64;
          break;
        case "QuarterThirtySeconds":
          tickSize = 1 / 128;
          break;
        case "EighthThirtySeconds":
          tickSize = 1 / 256;
          break;
        case "HalfSixtyFourths":
          tickSize = 1 / 128;
          break;
        default:
          tickSize = 0;
          break;
      }

      if (tickSize > 0) {
        let loopCount = 0;
        const baseVal = Math.round(val / tickSize) * tickSize;
        const checkVal = DecimalToFractionalConverter(baseVal, pType);
        if (FractionalToDecimalConverter(checkVal, pType).valid) {
          return checkVal;
        }

        while (loopCount < 50) {
          const nextValUp = baseVal + (loopCount + 1) * tickSize;
          const nextConvertedUp = DecimalToFractionalConverter(
            nextValUp,
            pType,
          );
          if (FractionalToDecimalConverter(nextConvertedUp, pType).valid) {
            return nextConvertedUp;
          }

          const nextValDown = baseVal - (loopCount + 1) * tickSize;
          const nextConvertedDown = DecimalToFractionalConverter(
            nextValDown,
            pType,
          );
          if (FractionalToDecimalConverter(nextConvertedDown, pType).valid) {
            return nextConvertedDown;
          }
          loopCount++;
        }
      }
    }
    return converted;
  };

  const onDidModalDismiss = () => {
    onDidDismiss();
    handleReset(true);
  };

  const getFractionalType = async () => {
    const accessToken = await getCapacitorPreference("accessToken");

    if (!accessToken) {
      setPriceType("NotFractional");
      return;
    }

    tradingGridGroupData &&
      send(config.socket.channels.CORE, {
        action: "getFractionalType",
        type: "getFractionalType",
        requestID: uuidv4(),
        payload: {
          accessToken: accessToken,
          productISIN: tradingGridGroupData.g,
        },
      });
  };

  const getInstrumentTradedPrice = (
    orderEntryData: any,
    type: string = "tradePrice",
  ) => {
    if (type === "bidPrice") {
      if (orderEntryData?.bb?.r > 0 || orderEntryData?.bb?.r < 0)
        return orderEntryData?.bb?.f;
      if (orderEntryData?.l?.r > 0 || orderEntryData?.l?.r < 0)
        return orderEntryData?.l?.f;
      return "0";
    }
    if (type === "offerPrice") {
      if (orderEntryData?.bo?.r > 0 || orderEntryData?.bo?.r < 0)
        return orderEntryData?.bo?.f;
      if (orderEntryData?.l?.r > 0 || orderEntryData?.l?.r < 0)
        return orderEntryData?.l?.f;
      return "0";
    }
    if (orderEntryData?.l?.r > 0 || orderEntryData?.l?.r < 0)
      return orderEntryData?.l?.f;
    else if (orderEntryData?.bb?.r > 0 || orderEntryData?.bb?.r < 0)
      return orderEntryData?.bb?.f;
    else if (orderEntryData?.bo?.r > 0 || orderEntryData?.bo?.r < 0)
      return orderEntryData?.bo?.f;
    else return "0";
  };

  const getInstrumentTradedPriceRaw = (
    orderEntryData: any,
    type: string = "tradePrice",
  ) => {
    if (type === "bidPrice") {
      if (orderEntryData?.bb?.r > 0 || orderEntryData?.bb?.r < 0)
        return orderEntryData?.bb?.r;
      if (orderEntryData?.l?.r > 0 || orderEntryData?.l?.r < 0)
        return orderEntryData?.l?.r;
      return 0;
    }
    if (type === "offerPrice") {
      if (orderEntryData?.bo?.r > 0 || orderEntryData?.bo?.r < 0)
        return orderEntryData?.bo?.r;
      if (orderEntryData?.l?.r > 0 || orderEntryData?.l?.r < 0)
        return orderEntryData?.l?.r;
      return 0;
    }
    if (orderEntryData?.l?.r > 0 || orderEntryData?.l?.r < 0)
      return orderEntryData?.l?.r;
    else if (orderEntryData?.bb?.r > 0 || orderEntryData?.bb?.r < 0)
      return orderEntryData?.bb?.r;
    else if (orderEntryData?.bo?.r > 0 || orderEntryData?.bo?.r < 0)
      return orderEntryData?.bo?.r;
    else return 0;
  };

  const extractOperatorFromCondition = (condition: string): string => {
    if (!condition) return "GTE";
    const match = condition.match(/(&[a-z]+;|[<>=]+)/);
    if (match) {
      const sym = match[1].replace("&gt;", ">").replace("&lt;", "<");
      const map: { [key: string]: string } = {
        ">=": "GTE",
        "<=": "LTE",
        ">": "GT",
        "<": "LT",
        "=": "EQ",
      };
      return map[sym] || "GTE";
    }
    return "GTE";
  };

  useEffect(() => {
    const checkPushPermissions = async () => {
      if (isOpen && Capacitor.isNativePlatform()) {
        try {
          const { value: hideWarning } = await Preferences.get({
            key: "hidePushWarning",
          });
          if (hideWarning === "true") {
            return;
          }

          let permStatus = await PushNotifications.checkPermissions();
          // if (permStatus.receive === "prompt") {
          //   permStatus = await PushNotifications.requestPermissions();
          // }
          if (permStatus.receive === "denied") {
            setShowPushWarning(true);
          } else if (permStatus.receive === "granted") {
            setShowPushWarning(false);
          }
        } catch (error) {
          console.error("Error checking push notification permissions:", error);
        }
      }
    };

    checkPushPermissions();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && tradingGridGroupData) {
      setPriceType(undefined);
      setTickGroup(undefined);
      if (tradingGridGroupData.ii !== 0) {
        const raw = getInstrumentTradedPriceRaw(tradingGridGroupData);
        fetchPriceTicks(raw, ["up", "down"]);
        setValue(getInstrumentTradedPrice(tradingGridGroupData));
        setValueRaw(raw);
      }

      if (tradingGridGroupData.ii === 0) {
        const fractionalTypeSuccessToken = PubSub.subscribe(
          pubsubEvents.socketKeys.CORE_GET_FRACTIONAL_TYPE_SUCCESS,
          (msg, data) => {
            setPriceType(data?.payload?.FractionalPriceType);
            // setTickGroup(data?.payload?.TickGroup);
            // setTickStrategy(data?.payload?.TickStrategy);
          },
        );

        const fractionalTypeFailureToken = PubSub.subscribe(
          pubsubEvents.socketKeys.CORE_GET_FRACTIONAL_TYPE_FAILURE,
          (msg, data) => {
            setPriceType("NotFractional");
          },
        );

        getFractionalType();

        return () => {
          PubSub.unsubscribe(fractionalTypeSuccessToken);
          PubSub.unsubscribe(fractionalTypeFailureToken);
        };
      } else {
        const contractsSuccessToken = PubSub.subscribe(
          pubsubEvents.socketKeys.CMED_ALL_CONTRACTS_SUCCESS,
          (msg, data) => {
            const payload = Array.isArray(data?.payload)
              ? data.payload[0]
              : data?.payload;
            setPriceType(payload?.FractionalPriceType);
            setTickGroup(payload?.TickGroup);
            setTickStrategy(payload?.TickStrategy);
          },
        );

        send(config.socket.channels.TRADING, {
          action: config.socket.actions.CMED_GET_CONTRACTS_BY_ITEM_ID,
          requestID: uuidv4(),
          type: "notUsedButMustBePresentForLegacyValidation",
          payload: {
            TradeableItemId: tradingGridGroupData?.ii?.toString(),
          },
        });

        return () => {
          PubSub.unsubscribe(contractsSuccessToken);
        };
      }
    }
  }, [isOpen, tradingGridGroupData]);

  useEffect(() => {
    if (
      priceType &&
      priceType !== "NotFractional" &&
      value !== undefined &&
      typeof value === "number" &&
      isPriceField(alertType)
    ) {
      setValueRaw(value);
      let convertedValue = getPaddedFractionalValue(value, priceType);
      setValue(convertedValue);
    }
  }, [priceType, value, alertType]);

  useEffect(() => {
    if (
      isOpen &&
      alertData &&
      priceType &&
      isPriceField(alertType) &&
      !initialValueSetRef.current
    ) {
      const rawVal = valueRaw;
      if (rawVal !== undefined) {
        if (priceType !== "NotFractional") {
          const convertedValue = getPaddedFractionalValue(rawVal, priceType);
          setValue(convertedValue);
        } else {
          setValue(rawVal);
        }
        initialValueSetRef.current = true;
      }
    }
  }, [isOpen, alertData, priceType, alertType, valueRaw]);

  useEffect(() => {
    const startSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_START_SUCCESS,
      (msg, data) => {
        // endMetric(MetricConstants.SET_ALERT_SUCCESS, { reqId: data.requestID });
        onDidModalDismiss();
      },
    );
    const editSuccessToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_EDIT_SUCCESS,
      (msg, data) => {
        // endMetric(MetricConstants.EDIT_ALERT_SUCCESS, {
        //   reqId: data.requestID,
        // });
        onDidModalDismiss();
      },
    );
    const startFailureToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_START_FAILURE,
      (msg, data) => {
        endMetric(MetricConstants.SET_ALERT_FAILURE, { reqId: data.requestID });
        setIsSubmitting(false);
      },
    );
    const editFailureToken = PubSub.subscribe(
      pubsubEvents.socketKeys.CORE_ALERT_EDIT_FAILURE,
      (msg, data) => {
        endMetric(MetricConstants.EDIT_ALERT_FAILURE, {
          reqId: data.requestID,
        });
        setIsSubmitting(false);
      },
    );

    if (alertData && isOpen) {
      setAlertName(alertData.alertName?.replaceAll("_", " ") || "");

      let op = "GT";
      let type: string = "tradePrice";
      let val: number | undefined = undefined;

      if (alertData.criteriaList && alertData.criteriaList.length > 0) {
        const firstCriteria = alertData.criteriaList[0];
        op = firstCriteria.operator;
        type = firstCriteria.field;
        val = firstCriteria.limitValue;
      } else {
        op = extractOperatorFromCondition(alertData.condition);
        type = alertData.type === "VOLUME" ? "tradePriceQty" : "tradePrice";
        val = extractValueFromCondition(alertData.condition);
      }

      if (!isEditMode) {
        if (op === "GTE" || op === "EQ") op = "GT";
        if (op === "LTE") op = "LT";
      }

      setOperator(op);
      setAlertType(type);
      setValueRaw(val);
      if (!isPriceField(type)) {
        setValue(val);
      } else {
        setValue(""); // Prevent flicker by not setting decimal value directly
      }
      setInitialFormState({
        alertName: alertData.alertName?.replaceAll("_", " ") || "",
        value: val,
        operator: op,
        alertType: type,
      });
      initialValueSetRef.current = false;
    } else if (isOpen && !isEditMode && tradingGridGroupData) {
      const now = new Date();
      const datePart = now.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      const timePart = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      const localDateTime = `${datePart}, ${timePart}`;
      setAlertName(`${tradingGridGroupData?.gi} - ${localDateTime}`);
    }

    return () => {
      PubSub.unsubscribe(startSuccessToken);
      PubSub.unsubscribe(editSuccessToken);
      PubSub.unsubscribe(startFailureToken);
      PubSub.unsubscribe(editFailureToken);
    };
  }, [isEditMode, alertData, isOpen, tradingGridGroupData]);

  const count = (n: string | number): number => {
    if (Number.isInteger(n)) {
      return 0;
    }
    let numStr = String(n);
    // Check for scientific notation (e.g., "5e-7", "1.23e-4")
    const lowerNumStr = numStr.toLowerCase();
    if (lowerNumStr.includes("e-")) {
      const parts = lowerNumStr.split("e-");
      const coefficient = parts[0];
      const exponent = parseInt(parts[1], 10);

      let decimalPlaces = exponent;
      if (coefficient.includes(".")) {
        const coefficientDecimalPart = coefficient.split(".")[1];
        decimalPlaces += coefficientDecimalPart.length;
      }
      return decimalPlaces;
    }

    if (numStr.includes(".")) {
      return numStr.split(".")[1].length;
    }
    return 0;
  };

  const isFormModified = React.useMemo(() => {
    if (!isEditMode || !initialFormState) return true;

    const getParsedValue = (val: string | number | undefined, type: string) => {
      if (typeof val === "number") return val;
      const rawVal = (val?.toString() || "").replace(/,/g, "");
      if (!rawVal || rawVal.trim() === "") return undefined;
      if (isPriceField(type) && priceType && priceType !== "NotFractional") {
        const { decimal_number } = FractionalToDecimalConverter(
          rawVal,
          priceType,
        );
        return isNaN(decimal_number) ? undefined : decimal_number;
      }
      return rawVal && !isNaN(parseFloat(rawVal))
        ? parseFloat(rawVal)
        : undefined;
    };

    const currentParsed = getParsedValue(value, alertType);
    const initialParsed = getParsedValue(
      initialFormState.value,
      initialFormState.alertType,
    );

    let valuesDiffer = currentParsed !== initialParsed;
    if (
      currentParsed !== undefined &&
      initialParsed !== undefined &&
      Math.abs(currentParsed - initialParsed) < 0.000001
    ) {
      valuesDiffer = false;
    }

    return (
      alertName !== initialFormState.alertName ||
      alertType !== initialFormState.alertType ||
      operator !== initialFormState.operator ||
      valuesDiffer
    );
  }, [
    isEditMode,
    initialFormState,
    alertName,
    alertType,
    operator,
    value,
    priceType,
  ]);

  function _beforePriceInput(e: any) {
    if (!isPriceField(alertType)) return;
    const inputElement = e.target as HTMLInputElement;
    const { selectionStart, selectionEnd, value: currentValue } = inputElement;
    const insertedData =
      priceType !== "NotFractional"
        ? (e.data || "").replace(".", "'")
        : (e.data || "").replace("", "");
    let potentialValueAfterOperation = currentValue;
    if (selectionStart !== null && selectionEnd !== null) {
      potentialValueAfterOperation =
        currentValue.substring(0, selectionStart) +
        insertedData +
        currentValue.substring(selectionEnd);
    }

    if (potentialValueAfterOperation.trim() === "" && insertedData === "") {
      return;
    }

    const parsedPriceString = potentialValueAfterOperation
      ?.toString()
      .replaceAll(",", "");
    const tickGroupInfo1 = parseTickGroupString(
      parseFloat(parsedPriceString),
      tickGroup,
    );
    let decimalPlaces;
    if (tickGroupInfo1.cabinetIndicator) {
      decimalPlaces = tickGroupInfo1.decimalPlaces;
    } else {
      let currentTickStrategy = tickStrategy;
      if (
        tradingGridGroupData?.c?.startsWith("ZC ") ||
        tradingGridGroupData?.c?.startsWith("ZW ") ||
        tradingGridGroupData?.c?.startsWith("ZS ")
      ) {
        currentTickStrategy = "0.25";
      }
      tickGroupInfo1.tickSize !== null && priceType === "NotFractional"
        ? (decimalPlaces = count(tickGroupInfo1.tickSize.toString()))
        : currentTickStrategy !== undefined
          ? (decimalPlaces = count(currentTickStrategy))
          : (decimalPlaces = 9);
    }

    const isInputFormatValidByRegex = PriceRegexValidator(
      potentialValueAfterOperation,
      priceType,
      decimalPlaces,
      "marketData",
    );

    if (!isInputFormatValidByRegex) {
      e.preventDefault();
    }
  }

  const fetchPriceTicks = (basePrice: number, direction: string[]) => {
    if (tradingGridGroupData && tradingGridGroupData.ii !== 0) {
      const requestId = uuidv4();
      priceRequestIds.current.push(requestId);
      send(config.socket.channels.TRADING, {
        action: config.socket.actions.CMED_ITEM_PRICES,
        requestID: requestId,
        type: "cmedGetPriceTicksRequest",
        payload: {
          itemId: tradingGridGroupData.ii,
          basePrice: basePrice,
          direction: direction,
        },
      });
    }
  };

  const updateTicks = (response: any) => {
    const pricesResponse = response.payload;
    if (pricesResponse.up !== undefined && pricesResponse.down !== undefined) {
      setTicks(pricesResponse);
    } else if (
      pricesResponse.up !== undefined ||
      pricesResponse.down !== undefined
    ) {
      setTicks((prevState: any) => {
        const newUp = pricesResponse.up
          ? removeDuplicatesFromTheFetchingTicks(
              prevState,
              pricesResponse.up,
              "up",
            ).up
          : prevState.up;
        const newDown = pricesResponse.down
          ? removeDuplicatesFromTheFetchingTicks(
              prevState,
              pricesResponse.down,
              "down",
            ).down
          : prevState.down;
        return { up: newUp, down: newDown };
      });
    }
  };

  useEffect(() => {
    const token = PubSub.subscribe(
      pubsubEvents.socketKeys.CMED_PRICE_TICKS_RESPONSE,
      (msg, data) => {
        if (priceRequestIds.current.includes(data.requestID)) {
          updateTicks(data);
        }
      },
    );
    return () => {
      PubSub.unsubscribe(token);
    };
  }, []);

  const handleSetAlert = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const formAlertName = formData.get("alertName") as string;
    const rawFormValue = (value?.toString() || "").replace(/,/g, "");
    let formValue: number | undefined;
    let isFractionalFormatValid = true;

    if (!rawFormValue || rawFormValue.trim() === "") {
      formValue = undefined;
    } else if (
      isPriceField(alertType) &&
      priceType &&
      priceType !== "NotFractional"
    ) {
      const { decimal_number, valid } = FractionalToDecimalConverter(
        rawFormValue,
        priceType,
      );
      isFractionalFormatValid = valid;
      formValue = isNaN(decimal_number) ? undefined : decimal_number;
    } else {
      formValue =
        rawFormValue && !isNaN(parseFloat(rawFormValue))
          ? parseFloat(rawFormValue)
          : undefined;
    }

    if (!formAlertName || formAlertName.trim() === "")
      setAlertNameRequiredErrorMessage("Alert name is required.");

    let isValueInvalid = false;
    if (formValue === undefined) {
      setValueRequiredErrorMessage("Value is required.");
      isValueInvalid = true;
    } else if (!isPriceField(alertType) && formValue < 0) {
      setValueRequiredErrorMessage(
        alertType === "totalVolume"
          ? "Volume must be greater than 0."
          : "Value must be greater than or equal to 0.",
      );
      isValueInvalid = true;
    } else if (
      !isPriceField(alertType) &&
      formValue == 0 &&
      operator !== "GT"
    ) {
      setValueRequiredErrorMessage(
        alertType === "totalVolume"
          ? "Volume must be greater than 0."
          : "Quantity condition must be greater than 0.",
      );
      isValueInvalid = true;
    } else if (alertType === "totalVolume" && formValue > 99999999) {
      setValueRequiredErrorMessage("Max allowed volume is 99999999.");
      isValueInvalid = true;
    } else if (
      !isPriceField(alertType) &&
      alertType !== "totalVolume" &&
      formValue > 99999
    ) {
      setValueRequiredErrorMessage("Max allowed quantity is 99999.");
      isValueInvalid = true;
    } else if (isPriceField(alertType) && formValue !== undefined) {
      if (!isFractionalFormatValid) {
        setValueRequiredErrorMessage("Price has to be of Type: " + priceType);
        isValueInvalid = true;
      } else {
        let tickSize: number | null = null;
        let min = -1000000;
        let max = 1000000;
        let cabinetError = false;

        if (tickGroup) {
          const tickGroupInfo = parseTickGroupString(formValue, tickGroup);
          if (tickGroupInfo.minimumValue !== null)
            min = tickGroupInfo.minimumValue;
          if (tickGroupInfo.maximumValue !== null)
            max = tickGroupInfo.maximumValue;
          tickSize = tickGroupInfo.tickSize;
          cabinetError = tickGroupInfo.cabinetError;
        }

        if (
          priceType === "Eighths" &&
          (tradingGridGroupData?.c?.startsWith("ZC ") ||
            tradingGridGroupData?.c?.startsWith("ZW ") ||
            tradingGridGroupData?.c?.startsWith("ZS "))
        ) {
          tickSize = 0.25;
        }

        if (formValue > max || formValue < min) {
          const outOfRangeMsg = cabinetError
            ? PriceErrorConstants.TICK_UP_TO_CABINET_PRICES_ERROR
            : `Price has to be between ${min.toString().includes("e") ? min.toFixed(Number(min.toString().split("-")[1])) : DecimalToFractionalConverter(min, priceType)} and ${max}.`;
          setValueRequiredErrorMessage(outOfRangeMsg);
          isValueInvalid = true;
        } else if (
          tickSize !== null &&
          !validatePrice(formValue, tickSize, priceType)
        ) {
          setValueRequiredErrorMessage(
            priceType === "NotFractional"
              ? `Tick Size: ${tickSize}`
              : "Price has to be of Type: " + priceType,
          );
          isValueInvalid = true;
        }
      }
    }

    if (
      !formAlertName ||
      formAlertName.trim() === "" ||
      isValueInvalid ||
      !tradingGridGroupData
    )
      return;

    hapticsImpactMedium();
    const { value: accessToken } = await Preferences.get({
      key: "accessToken",
    });
    if (!accessToken) return;

    const productISIN = tradingGridGroupData.g;
    const productCode = tradingGridGroupData.c.split(" ")[0];
    const reqId = isEditMode && alertData ? alertData.reqId : uuidv4();
    const action = isEditMode ? "alertEditRequest" : "alertStartRequest";
    const metricName = isEditMode
      ? MetricConstants.EDIT_ALERT
      : MetricConstants.SET_ALERT;
    startMetric(metricName, MetricTypeConstants.TIMER, { reqId });

    send(config.socket.channels.CORE, {
      action,
      type: action,
      requestID: reqId,
      payload: {
        accessToken,
        alertName: formAlertName,
        source: "CMEDMOBILE",
        reqId: isEditMode && alertData ? reqId : null,
        customData1: tradingGridGroupData?.c,
        criteriaList: [
          {
            securityId: parseInt(productISIN),
            productCode,
            field:
              alertType === "PRICE"
                ? "tradePrice"
                : alertType === "VOLUME"
                  ? "tradePriceQty"
                  : alertType,
            operator,
            limitValue: formValue,
          },
        ],
      },
    });

    CMEDfirebaseLogEvent(
      firebaseEventConfig.CMEDComponentNames.CMED_BEACON,
      firebaseEventConfig.eventNames.CMED_BEACON.CMED_BEACON_CREATE_ALERT,
    );

    setIsSubmitting(true);
    if (submissionTimeoutRef.current)
      clearTimeout(submissionTimeoutRef.current);
    submissionTimeoutRef.current = setTimeout(
      () => setIsSubmitting(false),
      5000,
    );
  };

  const handleReset = (isDismissing = false) => {
    if (isDismissing) {
      setAlertName("");
      setValue("0");
      setValueRaw(0);
      setOperator("GT");
      setAlertType("tradePrice");
      setProductType("FNO");
      setAlertNameRequiredErrorMessage("");
      setValueRequiredErrorMessage("");
      setTicks({ up: [], down: [] });
      setTickGroup(undefined);
      setPriceType(undefined);
      setTickStrategy(undefined);
      setIsSubmitting(false);
      initialValueSetRef.current = false;
      setInitialFormState(null);
      if (submissionTimeoutRef.current)
        clearTimeout(submissionTimeoutRef.current);
    } else if (alertData) {
      setAlertName(alertData.alertName?.replaceAll("_", " ") || "");
      let val: number | undefined;
      let op: string;
      let type: string;

      if (alertData.criteriaList && alertData.criteriaList.length > 0) {
        const firstCriteria = alertData.criteriaList[0];
        val = firstCriteria.limitValue;
        op = firstCriteria.operator;
        type = firstCriteria.field;
      } else {
        val = extractValueFromCondition(alertData.condition);
        op = extractOperatorFromCondition(alertData.condition);
        type = alertData.type === "VOLUME" ? "tradePriceQty" : "tradePrice";
      }

      if (!isEditMode) {
        if (op === "GTE" || op === "EQ") op = "GT";
        if (op === "LTE") op = "LT";
      }

      setOperator(op);

      setAlertType(type);
      setProductType("FNO");
      setValueRaw(val);
      if (isPriceField(type) && priceType && priceType !== "NotFractional") {
        if (val !== undefined) {
          setValue(getPaddedFractionalValue(val, priceType));
        }
      } else {
        setValue(val);
      }

      setInitialFormState({
        alertName: alertData.alertName?.replaceAll("_", " ") || "",
        value: val,
        operator: op,
        alertType: type,
      });
      initialValueSetRef.current = true;
      setAlertNameRequiredErrorMessage("");
      setValueRequiredErrorMessage("");
      setTicks({ up: [], down: [] });
      if (
        isPriceField(type) &&
        val !== undefined &&
        tradingGridGroupData &&
        tradingGridGroupData.ii !== 0
      ) {
        fetchPriceTicks(val, ["up", "down"]);
      }
    } else {
      setAlertName(initialFormState?.alertName || "");
      setOperator("GT");
      setAlertType("tradePrice");
      setProductType("FNO");
      setAlertNameRequiredErrorMessage("");
      setValueRequiredErrorMessage("");
      setTicks({ up: [], down: [] });
      if (tradingGridGroupData) {
        const now = new Date();
        const datePart = now.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
        const timePart = now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
        const localDateTime = `${datePart}, ${timePart}`;
        setAlertName(`${tradingGridGroupData?.gi} - ${localDateTime}`);
        setValue("0");
        setValueRaw(0);
        if (tradingGridGroupData.ii !== 0) {
          fetchPriceTicks(0, ["up", "down"]);
        }
      } else {
        setValue("0");
        setValueRaw(0);
      }
      setInitialFormState(null);
    }
  };

  const handleOpenSelect = (ref: React.RefObject<HTMLIonSelectElement>) => {
    if (ref.current) {
      ref.current.open();
      hapticsImpactMedium();
    }
  };

  const handleValueChange = (e: any) => {
    const input = e.target as HTMLIonInputElement;
    let rawValue = input.value as string;
    if (rawValue) setValueRequiredErrorMessage("");
    if (rawValue === null || rawValue === "") {
      setValueRaw(0);
      setValue("");
      setValueRequiredErrorMessage("Value is required.");
      fetchPriceTicks(0, ["up", "down"]);
      return;
    }

    const isOnlyDelimiter =
      rawValue === "'" ||
      rawValue === "." ||
      rawValue === "," ||
      rawValue === "-";

    if (isPriceField(alertType) && isOnlyDelimiter) {
      setValue(rawValue);
      setValueRaw(0);
      setValueRequiredErrorMessage(PriceErrorConstants.INVALID_PRICE_ERROR);
      fetchPriceTicks(0, ["up", "down"]);
      return;
    }

    let sanitizedValue = rawValue;
    if (isPriceField(alertType)) {
      if (priceType && priceType !== "NotFractional") {
        sanitizedValue = sanitizedValue.replace(/\./g, "'"); // User might type '.' which we convert to '
        sanitizedValue = sanitizedValue.replace(/[^0-9'-]/g, "");
      } else {
        sanitizedValue = sanitizedValue.replace(/[^0-9.-]/g, "");
      }
      const hasMinus = sanitizedValue.startsWith("-");
      sanitizedValue = sanitizedValue.replace(/-/g, "");
      if (hasMinus) {
        sanitizedValue = "-" + sanitizedValue;
      }
      if (priceType && priceType !== "NotFractional") {
        const parts = sanitizedValue.split("'");
        if (parts.length > 2) {
          sanitizedValue = (parts.shift() || "") + "'" + parts.join("");
        }
      } else {
        const parts = sanitizedValue.split(".");
        if (parts.length > 2) {
          sanitizedValue = (parts.shift() || "") + "." + parts.join("");
        }
      }
    } else {
      sanitizedValue = sanitizedValue.replace(/[^0-9]/g, "");
    }
    if (rawValue !== sanitizedValue) {
      input.value = sanitizedValue;
    }
    setValue(sanitizedValue);

    if (isPriceField(alertType) && tradingGridGroupData) {
      let raw = 0;
      let isFractionalValid = true;
      if (priceType && priceType !== "NotFractional") {
        const { decimal_number, valid } = FractionalToDecimalConverter(
          sanitizedValue,
          priceType,
        );
        raw = decimal_number;
        isFractionalValid = valid;
      } else {
        raw = parseFloat(sanitizedValue);
      }
      setValueRaw(raw);
      if (!isNaN(raw) && tradingGridGroupData.ii !== 0) {
        fetchPriceTicks(raw, ["up", "down"]);
      }

      if (!isFractionalValid) {
        setValueRequiredErrorMessage("Price has to be of Type: " + priceType);
      } else {
        let tickSize: number | null = null;
        let min = -1000000;
        let max = 1000000;
        let cabinetError = false;

        if (tickGroup) {
          const tickGroupInfo = parseTickGroupString(raw, tickGroup);
          if (tickGroupInfo.minimumValue !== null)
            min = tickGroupInfo.minimumValue;
          if (tickGroupInfo.maximumValue !== null)
            max = tickGroupInfo.maximumValue;
          tickSize = tickGroupInfo.tickSize;
          cabinetError = tickGroupInfo.cabinetError;
        }

        if (
          priceType === "Eighths" &&
          (tradingGridGroupData?.c?.startsWith("ZC ") ||
            tradingGridGroupData?.c?.startsWith("ZW ") ||
            tradingGridGroupData?.c?.startsWith("ZS "))
        ) {
          tickSize = 0.25;
        }

        if (raw > max || raw < min) {
          const outOfRangeMsg = cabinetError
            ? PriceErrorConstants.TICK_UP_TO_CABINET_PRICES_ERROR
            : `Price has to be between ${min.toString().includes("e") ? min.toFixed(Number(min.toString().split("-")[1])) : DecimalToFractionalConverter(min, priceType)} and ${max}.`;
          setValueRequiredErrorMessage(outOfRangeMsg);
          fetchPriceTicks(0, ["up", "down"]);
        } else if (tickSize !== null) {
          const isValid = validatePrice(raw, tickSize, priceType);
          if (!isValid) {
            setValueRequiredErrorMessage(
              priceType === "NotFractional"
                ? `Tick Size: ${tickSize}`
                : "Price has to be of Type: " + priceType,
            );
          }
        }
      }
    } else if (!isPriceField(alertType)) {
      const raw = parseFloat(sanitizedValue);
      setValueRaw(isNaN(raw) ? 0 : raw);
      if (alertType === "totalVolume") {
        if (raw > 99999999) {
          setValueRequiredErrorMessage("Max allowed volume is 99999999.");
        }
      } else {
        if (raw > 99999) {
          setValueRequiredErrorMessage("Max allowed quantity is 99999.");
        }
      }
    }
  };

  const handleTickClick = (obj: { dir: "up" | "down"; number: number }) => {
    if (!isPriceField(alertType)) {
      setValueRequiredErrorMessage("");
      let currentValue = parseFloat(value?.toString().replace(/,/g, "") || "0");
      if (isNaN(currentValue)) currentValue = 0;

      const maxLimit = alertType === "totalVolume" ? 99999999 : 99999;

      if (currentValue > maxLimit) {
        setValue("0");
      } else if (obj.dir === "up") {
        const newValue = currentValue + obj.number;
        setValue((newValue > maxLimit ? maxLimit : newValue).toString());
      } else {
        const newValue = currentValue - obj.number;
        setValue((newValue < 0 ? 0 : newValue).toString());
      }
    } else {
      if (
        valueRequiredErrorMessage &&
        (valueRequiredErrorMessage.includes("Price has to be between") ||
          valueRequiredErrorMessage.includes("Price is out of range"))
      ) {
        setValueRequiredErrorMessage("");

        if (tradingGridGroupData && tradingGridGroupData.ii !== 0) {
          const direction = obj.dir;
          const tickList = ticks[direction];
          let nextTick = null;

          if (tickList && tickList.length > 0) {
            if (direction === "up") {
              nextTick = tickList.find((t: any) => t.r > 0.0000001);
            } else {
              nextTick = tickList.find((t: any) => t.r < -0.0000001);
            }
          }

          if (nextTick) {
            setValue(nextTick.f);
            setValueRaw(nextTick.r);
            fetchPriceTicks(nextTick.r, ["up", "down"]);
          } else {
            const zeroString =
              priceType && priceType !== "NotFractional"
                ? getPaddedFractionalValue(0, priceType)
                : "0";
            setValue(zeroString);
            setValueRaw(0);
            fetchPriceTicks(0, ["up", "down"]);
          }
        } else {
          // Manual tick calculation for copy/edit mode
          let tickSize = 0;
          if (priceType && priceType !== "NotFractional") {
            switch (priceType) {
              case "Halves":
                tickSize = 1 / 2;
                break;
              case "Eighths":
                tickSize = 1 / 8;
                if (
                  tradingGridGroupData?.c?.startsWith("ZC ") ||
                  tradingGridGroupData?.c?.startsWith("ZW ") ||
                  tradingGridGroupData?.c?.startsWith("ZS ")
                ) {
                  tickSize = 0.25;
                }
                break;
              case "ThirtySeconds":
                tickSize = 1 / 32;
                break;
              case "SixtyFourths":
                tickSize = 1 / 64;
                break;
              case "HalfThirtySeconds":
                tickSize = 1 / 64;
                break;
              case "QuarterThirtySeconds":
                tickSize = 1 / 128;
                break;
              case "EighthThirtySeconds":
                tickSize = 1 / 256;
                break;
              case "HalfSixtyFourths":
                tickSize = 1 / 128;
                break;
              default:
                tickSize = 1;
                break;
            }
          }

          if (tickSize === 0 && tickGroup) {
            const info = parseTickGroupString(0, tickGroup);
            if (info.tickSize !== null) tickSize = info.tickSize;
          }

          if (tickSize === 0) tickSize = 1;

          let nextVal = obj.dir === "up" ? tickSize : -tickSize;

          if (priceType && priceType !== "NotFractional") {
            setValue(getPaddedFractionalValue(nextVal, priceType));
          } else {
            const decimals =
              tickSize > 0 && tickSize % 1 !== 0
                ? tickSize.toString().split(".")[1]?.length || 0
                : 0;
            const fixedVal = nextVal.toFixed(decimals);
            setValue(fixedVal);
            nextVal = parseFloat(fixedVal);
          }
          setValueRaw(nextVal);
        }
        return;
      }

      let currentRaw = 0;
      // let isInputValid = true;
      if (valueRequiredErrorMessage) {
        setValueRequiredErrorMessage("");
      }
      if (value !== undefined && value !== "") {
        const valStr = value.toString();
        const matchingTick = [...ticks.up, ...ticks.down].find(
          (t: any) => t.f === valStr,
        );
        if (matchingTick) {
          currentRaw = matchingTick.r;
        } else if (valueRaw !== undefined) {
          currentRaw = valueRaw;
        } else if (priceType && priceType !== "NotFractional") {
          const { decimal_number, valid } = FractionalToDecimalConverter(
            valStr,
            priceType,
          );
          currentRaw = decimal_number;

          if (priceType === "EighthThirtySeconds" && valStr.includes("'")) {
            const parts = valStr.split("'");
            if (parts.length === 2 && parts[1].length === 3) {
              const integer = parseInt(parts[0], 10);
              const num = parseInt(parts[1].substring(0, 2), 10);
              const sub = parseInt(parts[1].substring(2), 10);
              if (!isNaN(integer) && !isNaN(num) && !isNaN(sub)) {
                const sign = valStr.startsWith("-") ? -1 : 1;
                let subVal = sub;
                if (sub === 5) subVal = 4;
                else if (sub === 7) subVal = 6;
                currentRaw =
                  sign * (Math.abs(integer) + num / 32 + subVal / 256);
              }
            }
          }
        } else {
          currentRaw = parseFloat(valStr.replace(/,/g, ""));
        }
      } else {
        currentRaw = 0;
      }

      if (isNaN(currentRaw)) return;

      if (tradingGridGroupData && tradingGridGroupData.ii !== 0) {
        const list = ticks[obj.dir];
        let nextTick = null;

        if (obj.dir === "up") {
          const candidates = list
            .filter((t: any) => t.r > currentRaw + 0.0000001)
            .sort((a: any, b: any) => a.r - b.r);
          if (candidates.length > 0) nextTick = candidates[0];
        } else {
          const candidates = list
            .filter((t: any) => t.r < currentRaw - 0.0000001)
            .sort((a: any, b: any) => b.r - a.r);
          if (candidates.length > 0) nextTick = candidates[0];
        }

        if (nextTick) {
          if (
            (obj.dir === "down" &&
              currentRaw > 0.0000001 &&
              nextTick.r < -0.0000001) ||
            (obj.dir === "up" &&
              currentRaw < -0.0000001 &&
              nextTick.r > 0.0000001)
          ) {
            let isZeroValid = true;
            if (tickGroup) {
              const info = parseTickGroupString(0, tickGroup);
              const min =
                info.minimumValue !== null ? info.minimumValue : -1000000;
              const max =
                info.maximumValue !== null ? info.maximumValue : 1000000;
              if (0 < min || 0 > max) isZeroValid = false;
            }

            if (isZeroValid) {
              let f = "0";
              if (priceType && priceType !== "NotFractional") {
                f = DecimalToFractionalConverter(0, priceType);
              }
              nextTick = { r: 0, f: f };
            }
          }
          let nextTickValue = nextTick.f;
          setValueRequiredErrorMessage("");
          setValue(nextTickValue);
          setValueRaw(nextTick.r);
          fetchPriceTicks(nextTick.r, ["up", "down"]);
        } else {
          fetchPriceTicks(currentRaw, ["up", "down"]);
        }
        return;
      }

      let tickSize = 0;
      if (priceType && priceType !== "NotFractional") {
        switch (priceType) {
          case "Halves":
            tickSize = 1 / 2;
            break;
          case "Eighths":
            tickSize = 1 / 8;
            if (
              tradingGridGroupData?.c?.startsWith("ZC ") ||
              tradingGridGroupData?.c?.startsWith("ZW ") ||
              tradingGridGroupData?.c?.startsWith("ZS ")
            ) {
              // These products trade in 1/4 cent increments (0.25) but are quoted in eighths.
              tickSize = 0.25;
            }
            break;
          case "ThirtySeconds":
            tickSize = 1 / 32;
            break;
          case "SixtyFourths":
            tickSize = 1 / 64;
            break;
          case "HalfThirtySeconds":
            tickSize = 1 / 64;
            break;
          case "QuarterThirtySeconds":
            tickSize = 1 / 128;
            break;
          case "EighthThirtySeconds":
            tickSize = 1 / 256;
            break;
          case "HalfSixtyFourths":
            tickSize = 1 / 128;
            break;
          // case "OneHundredth":
          //   tickSize = 0.01;
          //   break;
          // case "OneThousandth":
          //   tickSize = 0.001;
          //   break;
          // case "TenThousandth":
          //   tickSize = 0.0001;
          //   break;
          default:
            tickSize = 1;
            break;
        }
      }

      if (tickSize === 0 && tickGroup) {
        const info = parseTickGroupString(currentRaw, tickGroup);
        if (info.tickSize !== null) tickSize = info.tickSize;
      }

      let explicitTickSize = 0;
      if (priceType === undefined || priceType === "NotFractional") {
        let s = currentRaw.toString();
        if (value !== undefined && value !== null) {
          const valStr = value.toString().replace(/,/g, "");
          const parsedVal = parseFloat(valStr);
          if (
            !isNaN(parsedVal) &&
            Math.abs(parsedVal - currentRaw) < 0.000001
          ) {
            s = valStr;
          }
        }
        if (s.includes(".")) {
          let decimals = s.split(".")[1].length;
          if (decimals === 0) decimals = 1;
          explicitTickSize = Math.pow(10, -decimals);
        }
      }

      if (tickSize === 0) {
        tickSize = explicitTickSize > 0 ? explicitTickSize : 1;
      } else if (explicitTickSize > 0 && explicitTickSize < tickSize) {
        tickSize = explicitTickSize;
      }
      let nextVal =
        obj.dir === "up" ? currentRaw + tickSize : currentRaw - tickSize;

      if (
        (obj.dir === "down" &&
          currentRaw > 0.0000001 &&
          nextVal < -0.0000001) ||
        (obj.dir === "up" && currentRaw < -0.0000001 && nextVal > 0.0000001)
      ) {
        nextVal = 0;
      }

      const nextFracValue = getPaddedFractionalValue(
        nextVal,
        priceType || "NotFractional",
      );

      const { decimal_number, valid } = FractionalToDecimalConverter(
        nextFracValue,
        priceType,
      );

      if (!valid && tickSize > 0) {
        let loopCount = 0;
        while (loopCount < 50) {
          nextVal = obj.dir === "up" ? nextVal + tickSize : nextVal - tickSize;
          const nextFrac = getPaddedFractionalValue(
            nextVal,
            priceType || "NotFractional",
          );
          const { valid: isValid } = FractionalToDecimalConverter(
            nextFrac,
            priceType,
          );
          if (isValid) break;
          loopCount++;
        }
      } else {
        if (
          tickSize > 0 &&
          Math.abs(nextVal - decimal_number) < tickSize * 0.5
        ) {
          nextVal = decimal_number;
        }
      }

      if (priceType && priceType !== "NotFractional") {
        let convertedValue = getPaddedFractionalValue(nextVal, priceType);
        setValue(convertedValue);
      } else {
        let decimals = 0;
        if (tickSize % 1 !== 0) {
          decimals = tickSize.toString().split(".")[1]?.length || 0;
        }
        const fixedVal = nextVal.toFixed(decimals);
        setValue(fixedVal);
        nextVal = parseFloat(fixedVal);
      }
      setValueRaw(nextVal);
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDidModalDismiss}>
      <IonHeader className="ion-no-border">
        <IonToolbar>
          <IonTitle>
            <p className="ion-no-margin text-bold">
              {isEditMode ? "EDIT ALERT" : "CREATE ALERT"}
            </p>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDidDismiss} slot="icon-only" fill="clear">
              <IonIcon icon={closeOutline} color="dark" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <form
          id="alert-form"
          onSubmit={handleSetAlert}
          style={{ padding: "13px" }}
        >
          <IonGrid className="ion-no-padding">
            <IonRow>
              <IonCol size="12">
                <p
                  className="ion-no-margin text-bold"
                  style={{ fontSize: "10px", marginBottom: "10px" }}
                >
                  ALERT NAME
                </p>
                <div
                  style={{
                    display: "flex",
                    height: "41px",
                    alignItems: "center",
                    padding: "12px 16px",
                    backgroundColor: alertNameRequiredErrorMessage
                      ? "rgba(232, 61, 61, 0.086)"
                      : "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.10)",
                  }}
                >
                  <IonInput
                    clearInput={true}
                    clearInputIcon={close}
                    name="alertName"
                    value={alertName}
                    placeholder="Enter alert name..."
                    onIonInput={(e) => {
                      const input = e.target as HTMLIonInputElement;
                      const rawValue = (input.value as string) || "";
                      // const sanitizedValue = rawValue;
                      // if (rawValue !== sanitizedValue) {
                      //   input.value = sanitizedValue;
                      // }
                      // setAlertName(sanitizedValue);
                      // setAlertNameRequiredErrorMessage(
                      //   sanitizedValue.trim() ? "" : "Alert name is required.",
                      // );
                      setAlertName(rawValue);
                      setAlertNameRequiredErrorMessage(
                        rawValue.trim() ? "" : "Alert name is required.",
                      );
                    }}
                  />
                </div>
                {alertNameRequiredErrorMessage && (
                  <IonText color="danger">
                    <p style={{ fontSize: "12px", marginTop: "5px" }}>
                      {alertNameRequiredErrorMessage}
                    </p>
                  </IonText>
                )}
              </IonCol>
            </IonRow>
            <IonRow style={{ marginTop: "20px" }}>
              <IonCol size="12">
                <p
                  className="ion-no-margin text-bold"
                  style={{ fontSize: "10px", marginBottom: "10px" }}
                >
                  PRODUCT NAME
                </p>
                <div
                  style={{
                    display: "flex",
                    height: "41px",
                    alignItems: "center",
                    padding: "12px 16px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.10)",
                    opacity: 0.6,
                  }}
                >
                  <IonInput
                    value={
                      alertData?.customData1
                        ? alertData?.customData1
                        : isEditMode || alertData
                          ? tradingGridGroupData?.c?.split(" ")[1]
                          : (tradingGridGroupData?.c ?? "")
                    }
                    readonly
                  />
                </div>
              </IonCol>
            </IonRow>
            <IonRow style={{ marginTop: "20px" }}>
              <IonCol size="6" style={{ paddingRight: "10px" }}>
                <p
                  className="ion-no-margin text-bold"
                  style={{ fontSize: "10px", marginBottom: "10px" }}
                >
                  TRIGGER FIELD
                </p>
                <div
                  onClick={() => handleOpenSelect(alertTypeRef)}
                  style={{
                    display: "flex",
                    height: "41px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.10)",
                  }}
                >
                  <p className="ion-no-margin" style={{ fontSize: "14px" }}>
                    {getFieldLabel(alertType)}
                  </p>
                  <IonIcon
                    icon={expandIcon}
                    style={{ height: "20px", width: "20px" }}
                  />
                </div>
                <IonSelect
                  ref={alertTypeRef}
                  className="ion-hide"
                  value={alertType}
                  interface="action-sheet"
                  onIonChange={(e) => {
                    const newType = e.detail.value;
                    setAlertType(newType);
                    setValueRequiredErrorMessage("");

                    if (alertData) {
                      const alertDataResolvedType =
                        alertData.criteriaList?.[0]?.field ||
                        (alertData.type === "VOLUME"
                          ? "tradePriceQty"
                          : "tradePrice");
                      if (newType === alertDataResolvedType) {
                        const originalVal =
                          alertData.criteriaList?.[0]?.limitValue !== undefined
                            ? alertData.criteriaList[0].limitValue
                            : extractValueFromCondition(alertData.condition);
                        setValueRaw(originalVal);
                        setValue(
                          isPriceField(newType) && originalVal !== undefined
                            ? originalVal
                            : originalVal?.toString() || "0",
                        );
                        if (
                          isPriceField(newType) &&
                          originalVal !== undefined &&
                          tradingGridGroupData?.ii !== 0
                        ) {
                          fetchPriceTicks(originalVal, ["up", "down"]);
                        }
                      } else {
                        if (isPriceField(newType)) {
                          const price = getInstrumentTradedPrice(
                            tradingGridGroupData,
                            newType,
                          );
                          const raw = getInstrumentTradedPriceRaw(
                            tradingGridGroupData,
                            newType,
                          );
                          setValue(price);
                          setValueRaw(raw);
                          if (tradingGridGroupData?.ii !== 0) {
                            fetchPriceTicks(raw, ["up", "down"]);
                          }
                        } else {
                          if (newType === "totalVolume") {
                            setValue(tradingGridGroupData?.t?.f || "0");
                            setValueRaw(tradingGridGroupData?.t?.r || 0);
                          } else {
                            setValue("0");
                            setValueRaw(0);
                          }
                        }
                      }
                    } else {
                      if (isPriceField(newType)) {
                        const price = getInstrumentTradedPrice(
                          tradingGridGroupData,
                          newType,
                        );
                        const raw = getInstrumentTradedPriceRaw(
                          tradingGridGroupData,
                          newType,
                        );
                        setValue(price);
                        setValueRaw(raw);
                        if (tradingGridGroupData?.ii !== 0) {
                          fetchPriceTicks(raw, ["up", "down"]);
                        }
                      } else {
                        if (newType === "totalVolume") {
                          setValue(tradingGridGroupData?.t?.f || "0");
                          setValueRaw(tradingGridGroupData?.t?.r || 0);
                        } else {
                          setValue("0");
                          setValueRaw(0);
                        }
                      }
                    }
                    hapticsImpactMedium();
                  }}
                >
                  <IonSelectOption value="tradePrice">
                    Trade Price
                  </IonSelectOption>
                  <IonSelectOption value="tradePriceQty">
                    Trade Qty
                  </IonSelectOption>
                  <IonSelectOption value="bidPrice">Bid Price</IonSelectOption>
                  <IonSelectOption value="bidPriceQty">Bid Qty</IonSelectOption>
                  <IonSelectOption value="offerPrice">
                    Offer Price
                  </IonSelectOption>
                  <IonSelectOption value="offerPriceQty">
                    Offer Qty
                  </IonSelectOption>
                  <IonSelectOption value="totalVolume">Volume</IonSelectOption>
                </IonSelect>
              </IonCol>
              <IonCol size="6" style={{ paddingLeft: "10px" }}>
                <p
                  className="ion-no-margin text-bold"
                  style={{ fontSize: "10px", marginBottom: "10px" }}
                >
                  OPERATOR
                </p>
                <div
                  onClick={() => handleOpenSelect(operatorRef)}
                  style={{
                    display: "flex",
                    height: "41px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.10)",
                  }}
                >
                  <p className="ion-no-margin" style={{ fontSize: "14px" }}>
                    {operator === "GTE"
                      ? "≥"
                      : operator === "LTE"
                        ? "≤"
                        : operator === "GT"
                          ? ">"
                          : operator === "LT"
                            ? "<"
                            : "="}
                  </p>
                  <IonIcon
                    icon={expandIcon}
                    style={{ height: "20px", width: "20px" }}
                  />
                </div>
                <IonSelect
                  ref={operatorRef}
                  className="ion-hide"
                  value={operator}
                  interface="action-sheet"
                  onIonChange={(e) => {
                    const newOperator = e.detail.value;
                    setOperator(newOperator);
                    if (
                      !isPriceField(alertType) &&
                      newOperator === "GT" &&
                      (valueRequiredErrorMessage ===
                        "Quantity condition must be greater than 0." ||
                        valueRequiredErrorMessage ===
                          "Volume must be greater than 0.")
                    ) {
                      setValueRequiredErrorMessage("");
                    }
                    hapticsImpactMedium();
                  }}
                >
                  {isEditMode && initialFormState?.operator === "GTE" && (
                    <IonSelectOption value="GTE">≥</IonSelectOption>
                  )}
                  {isEditMode && initialFormState?.operator === "LTE" && (
                    <IonSelectOption value="LTE">≤</IonSelectOption>
                  )}
                  {isEditMode && initialFormState?.operator === "EQ" && (
                    <IonSelectOption value="EQ">=</IonSelectOption>
                  )}
                  <IonSelectOption value="GT">&gt;</IonSelectOption>
                  <IonSelectOption value="LT">&lt;</IonSelectOption>
                </IonSelect>
              </IonCol>
            </IonRow>
            <IonRow style={{ marginTop: "20px" }}>
              <IonCol size="12">
                <p
                  className="ion-no-margin text-bold"
                  style={{ fontSize: "10px", marginBottom: "10px" }}
                >
                  VALUE
                </p>
                <TickComponent
                  name={getFieldLabel(alertType)}
                  value={value}
                  handleChange={handleValueChange}
                  handleClick={handleTickClick}
                  tickDisabled={
                    isPriceField(alertType) && !tradingGridGroupData
                  }
                  onKeyPressFunc={
                    !isPriceField(alertType) ? _handleKeyDownForQty : undefined
                  }
                  errorFlag={!!valueRequiredErrorMessage}
                  onFocus={(e: any) => {
                    if (
                      isPriceField(alertType) &&
                      (!priceType || priceType === "NotFractional")
                    ) {
                      removeCommasOnFocus(
                        e,
                        "price",
                        setValue,
                        setValue,
                        "NotFractional",
                      );
                    }
                  }}
                  onFocusOut={(e: any) => {
                    if (isPriceField(alertType)) {
                      const value = e.target.value as string;
                      if (
                        !value ||
                        value.endsWith("'") ||
                        value.startsWith("'")
                      ) {
                        return;
                      }
                      if (priceType && priceType !== "NotFractional") {
                        const valueWithoutCommas = value.replace(/,/g, "");
                        const { decimal_number, valid } =
                          FractionalToDecimalConverter(
                            valueWithoutCommas,
                            priceType,
                          );
                        if (valid && !isNaN(decimal_number)) {
                          const formattedPrice = getPaddedFractionalValue(
                            decimal_number,
                            priceType,
                          );
                          setValue(formattedPrice);
                        }
                      }
                    }
                  }}
                  onBeforeInput={_beforePriceInput}
                />
                {valueRequiredErrorMessage && (
                  <IonText color="danger">
                    <p style={{ fontSize: "12px", marginTop: "5px" }}>
                      {valueRequiredErrorMessage}
                    </p>
                  </IonText>
                )}
              </IonCol>
            </IonRow>
          </IonGrid>
        </form>
      </IonContent>
      <IonFooter
        className="ion-no-padding ion-no-border"
        style={{ marginBottom: "20px" }}
      >
        <IonGrid className="ion-padding">
          <IonRow>
            <IonCol size="6">
              <IonButton
                className="custom_cmed_mobile_button"
                expand="block"
                fill="outline"
                color="primary"
                onClick={() => {
                  handleReset();
                  hapticsImpactMedium();
                }}
              >
                RESET
              </IonButton>
            </IonCol>
            <IonCol size="6">
              <IonButton
                className="custom_cmed_mobile_button"
                expand="block"
                type="submit"
                form="alert-form"
                disabled={isSubmitting || (isEditMode && !isFormModified)}
                color="primary"
              >
                {isSubmitting
                  ? "SUBMITTING..."
                  : isEditMode
                    ? "UPDATE ALERT"
                    : "CREATE ALERT"}
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonFooter>
      <IonAlert
        className="one-alert"
        isOpen={showPushWarning}
        onDidDismiss={() => setShowPushWarning(false)}
        header="Enable Notifications"
        message="Push notifications are disabled. You will not be able to receive alerts until they are enabled in your device settings."
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
            cssClass: "alert-button-cancel",
            handler: async () => {
              await Preferences.set({
                key: "hidePushWarning",
                value: "true",
              });
            },
          },
          {
            text: "Settings",
            cssClass: "alert-button-confirm",
            handler: () => {
              NativeSettings.open({
                optionAndroid: AndroidSettings.AppNotification,
                optionIOS: IOSSettings.App,
              });
            },
          },
        ]}
      />
    </IonModal>
  );
};

export default memo(PriceAlertModal);
